import React, { useState, useEffect } from "react";
import { TicketOption } from "../../types";
import {
  createTicket,
  updateTicket,
  deleteAdminTicket,
  getAdminTickets,
} from "../../services/api/admin/tickets";
import {
  Plus,
  Trash2,
  Tag,
  Edit2,
  CheckCircle2,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { formatIDR, parseIDR } from "../../services/utils";
import Swal from "sweetalert2";

interface TicketManagementProps {
  onRefresh?: () => void;
}

export const TicketManagement: React.FC<TicketManagementProps> = ({
  onRefresh,
}) => {
  const [tickets, setTickets] = useState<TicketOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const data = await getAdminTickets();
      setTickets(data);
    } catch (error) {
      console.error("Failed to fetch tickets:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const internalRefresh = () => {
    fetchData();
    if (onRefresh) onRefresh();
  };

  const initialFormState: TicketOption = {
    id: "",
    title: "",
    description: "",
    price: 0,
    // originalPrice: 0,
    features: [],
    recommended: false,
    type: "regular",
  };

  const [form, setForm] = useState<TicketOption>(initialFormState);
  const [featureInput, setFeatureInput] = useState("");

  const openModal = (ticket?: TicketOption) => {
    if (ticket) {
      setForm(ticket);
      setIsEditing(true);
    } else {
      setForm({ ...initialFormState, id: `t_${Date.now()}` });
      setIsEditing(false);
    }
    setFeatureInput("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setForm(initialFormState);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const ticketData = {
        name: form.title,
        price: form.price,
        type: form.type as "regular" | "fast-track",
        description: form.description,
        ticketFeatures: form.features,
        isActive: true,
        isRecommended: form.recommended,
        originalPrice: form.originalPrice,
      };

      if (isEditing) {
        await updateTicket(form.id, ticketData);
        Swal.fire({
          icon: "success",
          title: "Tiket Diperbarui",
          text: "Detail tiket berhasil disimpan.",
          timer: 2000,
          showConfirmButton: false,
          background: "#FFFFFF",
        });
      } else {
        await createTicket(ticketData);
        Swal.fire({
          icon: "success",
          title: "Tiket Dibuat",
          text: "Tiket baru berhasil ditambahkan.",
          timer: 2000,
          showConfirmButton: false,
          background: "#FFFFFF",
        });
      }
      internalRefresh();
      closeModal();
    } catch (error) {
      console.error("Failed to save ticket:", error);
      Swal.fire({
        icon: "error",
        title: "Gagal Menyimpan",
        text: "Terjadi kesalahan saat menyimpan tiket. Silakan coba lagi.",
        confirmButtonColor: "#0F172A",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: "Hapus Tiket?",
      text: "Data yang dihapus tidak bisa dikembalikan!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#64748B",
      confirmButtonText: "Ya, Hapus!",
      cancelButtonText: "Batal",
      reverseButtons: true,
      background: "#FFFFFF",
    });

    if (result.isConfirmed) {
      try {
        await deleteAdminTicket(id);
        Swal.fire({
          icon: "success",
          title: "Terhapus!",
          text: "Tiket telah berhasil dihapus.",
          timer: 1500,
          showConfirmButton: false,
        });
        internalRefresh();
      } catch (error) {
        console.error("Failed to delete ticket:", error);
        Swal.fire({
          icon: "error",
          title: "Gagal Menghapus",
          text: "Terjadi kesalahan saat menghapus tiket.",
          confirmButtonColor: "#0F172A",
        });
      }
    }
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setForm((prev) => ({
        ...prev,
        features: [...prev.features, featureInput.trim()],
      }));
      setFeatureInput("");
    }
  };

  const removeFeature = (index: number) => {
    setForm((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Manajemen Tiket</h2>
          <p className="text-gray-500 text-sm mt-1">
            Kelola jenis tiket, harga, dan fitur yang ditampilkan di halaman
            booking.
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className="bg-gray-900 text-white px-4 py-2 rounded-lg font-bold hover:bg-black flex items-center gap-2 text-sm"
        >
          <Plus className="w-4 h-4" /> Tambah Tiket
        </button>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center p-20 text-gray-400 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <Loader2 className="w-10 h-10 animate-spin mb-4" />
          <p className="font-bold font-mono uppercase tracking-widest text-xs">
            Synchronizing Tickets...
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-500 font-bold border-b border-gray-200">
              <tr>
                <th className="px-6 py-4">Nama Tiket</th>
                <th className="px-6 py-4">Harga Jual</th>
                <th className="px-6 py-4">Harga Coret</th>
                <th className="px-6 py-4">Kategori</th>
                <th className="px-6 py-4">Fitur</th>
                <th className="px-6 py-4 text-center">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tickets.map((ticket) => (
                <tr
                  key={ticket.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="font-bold text-gray-900">
                      {ticket.title}
                    </div>
                    <div className="text-xs text-gray-500 truncate max-w-[200px]">
                      {ticket.description}
                    </div>
                  </td>
                  <td className="px-6 py-4 font-mono font-medium text-emerald-600 font-bold">
                    Rp {formatIDR(ticket.price)}
                  </td>
                  <td className="px-6 py-4 font-mono text-gray-400 line-through">
                    {ticket.originalPrice
                      ? `Rp ${formatIDR(ticket.originalPrice)}`
                      : "-"}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium capitalize ${
                        ticket.type === "fast-track"
                          ? "bg-orange-100 text-orange-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {ticket.type === "fast-track" ? "Fast Track" : "Reguler"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded-full text-xs font-bold">
                      {ticket.features.length} Fitur
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => openModal(ticket)}
                        className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(ticket.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {tickets.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-8 text-center text-gray-400"
                  >
                    Belum ada tiket yang dibuat.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Form */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 sticky top-0 z-10">
              <h3 className="font-bold text-lg text-gray-900">
                {isEditing ? "Edit Tiket" : "Buat Tiket Baru"}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <Plus className="w-6 h-6 rotate-45" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Nama Tiket
                </label>
                <input
                  type="text"
                  required
                  className="w-full border border-gray-300 rounded-lg p-2.5 bg-white text-gray-900 focus:ring-2 focus:ring-giz-primary outline-none"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                    Harga Jual (Rp)
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full border border-gray-300 rounded-lg p-2.5 bg-white text-gray-900 focus:ring-2 focus:ring-giz-primary outline-none"
                    value={formatIDR(form.price)}
                    onChange={(e) =>
                      setForm({ ...form, price: parseIDR(e.target.value) })
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                    Harga Coret (Rp) - Opsional
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg p-2.5 bg-white text-gray-900 focus:ring-2 focus:ring-giz-primary outline-none"
                    value={
                      form.originalPrice ? formatIDR(form.originalPrice) : ""
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        originalPrice: parseIDR(e.target.value),
                      })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                    Tipe
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg p-2.5 bg-white text-gray-900 focus:ring-2 focus:ring-giz-primary outline-none"
                    value={form.type}
                    onChange={(e) =>
                      setForm({ ...form, type: e.target.value as any })
                    }
                  >
                    <option value="regular">Regular</option>
                    <option value="fast-track">Fast Track</option>
                  </select>
                </div>
                <div className="flex items-end pb-1">
                  <div
                    className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer w-full"
                    onClick={() =>
                      setForm({ ...form, recommended: !form.recommended })
                    }
                  >
                    <div
                      className={`w-5 h-5 rounded border flex items-center justify-center ${
                        form.recommended
                          ? "bg-giz-primary border-giz-primary"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {form.recommended && (
                        <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                      )}
                    </div>
                    <span className="text-xs font-bold text-gray-700 uppercase">
                      Recommended
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Deskripsi Singkat
                </label>
                <textarea
                  required
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg p-2.5 bg-white text-gray-900 focus:ring-2 focus:ring-giz-primary outline-none"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                  Fitur Tiket (Per Baris)
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    placeholder="Contoh: Masuk GIZ Park..."
                    className="flex-1 border border-gray-300 rounded-lg p-2.5 bg-white text-gray-900 focus:ring-2 focus:ring-giz-primary outline-none text-sm"
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && (e.preventDefault(), addFeature())
                    }
                  />
                  <button
                    type="button"
                    onClick={addFeature}
                    className="bg-gray-900 text-white px-4 rounded-lg font-bold hover:bg-black"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.features.map((feat, idx) => (
                    <span
                      key={idx}
                      className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded-md text-xs font-bold flex items-center gap-1"
                    >
                      {feat}
                      <button
                        type="button"
                        onClick={() => removeFeature(idx)}
                        className="hover:text-red-500"
                      >
                        <Plus className="w-3 h-3 rotate-45" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-3 text-gray-500 font-bold hover:bg-gray-100 rounded-xl transition-colors"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-giz-primary text-white font-bold rounded-xl shadow-lg hover:bg-indigo-700 transition-colors"
                >
                  Simpan Tiket
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
